import ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";
import { ManholeViewStore } from "../Manhole/stores/ManholeViewStore";

const JobDetailRoute: RouteType = {
  name: "Job Detail",
  path: "/estimating/jobs/detail",
  layout: "/app",
  group: "Estimating",
  requiresAuth: true,
  requiresPermission: false,
  defaultQueryParams: { tab: "general" },
  viewportCard: {
    header: {
      title: "Job Detail",
      component: () => import("./JobDetailHeader"),
      enableRefreshButton: false,
      enableBackButton: true,
    },
    body: {
      component: () => import("./JobDetailBody"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore, queryParams }) => {
    try {
      const JobDetailViewStore = (await import("./JobDetailViewStore")).default;

      if (!(rootStore as any).jobDetailViewStore) {
        (rootStore as any).jobDetailViewStore = new JobDetailViewStore(
          rootStore
        );
      }

      const viewStore = (rootStore as any).jobDetailViewStore;
      const projectId = queryParams?.projectId as string;
      viewStore.setProjectId(projectId || null);

      const promises = [];

      // Load manhole projects
      const manholeProjectsStore =
        rootStore.manholeProjectsStore as ServiceStore;
      if (manholeProjectsStore) {
        promises.push(manholeProjectsStore.find());
      }

      // Load structures for this project
      const manholeStructuresStore =
        rootStore.manholeStructuresStore as ServiceStore;
      if (manholeStructuresStore && projectId) {
        promises.push(
          manholeStructuresStore.find({
            query: { projectId },
          })
        );
      }

      // Load quotes
      const manholeQuotesStore =
        rootStore.manholeQuotesStore as ServiceStore;
      if (manholeQuotesStore) {
        promises.push(manholeQuotesStore.find());
      }

      // Load sequences (components for structures)
      const manholeSequencesStore = (rootStore as any)
        .manholeSequencesStore as ServiceStore;
      if (manholeSequencesStore) {
        promises.push(manholeSequencesStore.find());
      }

      // Load pipe connections
      const manholePipeConnectionsStore = (rootStore as any)
        .manholePipeConnectionsStore as ServiceStore;
      if (manholePipeConnectionsStore) {
        promises.push(manholePipeConnectionsStore.find());
      }

      // Load internals
      const manholeInternalsStore = (rootStore as any)
        .manholeInternalsStore as ServiceStore;
      if (manholeInternalsStore) {
        promises.push(manholeInternalsStore.find());
      }

      // Load quote items for pricing detail
      const manholeQuoteItemsStore = (rootStore as any)
        .manholeQuoteItemsStore as ServiceStore;
      if (manholeQuoteItemsStore) {
        promises.push(manholeQuoteItemsStore.find());
      }

      // Load structure codes for code name lookup
      const structureCodesStore = (rootStore as any)
        .structureCodesStore as ServiceStore;
      if (structureCodesStore) {
        promises.push(structureCodesStore.find());
      }

      // Load customers
      const customersStore = rootStore.customersStore as ServiceStore;
      if (customersStore) {
        promises.push(customersStore.find());
      }

      await Promise.allSettled(promises);

      // Initialize manholeViewStore structure order so QuotePreviewDialog
      // can iterate structures when opened from Job Detail
      if (projectId) {
        const manholeViewStore = (rootStore as any)
          .manholeViewStore as ManholeViewStore;
        if (manholeViewStore) {
          await manholeViewStore.initializeStructureOrder(projectId);
        }
      }

      return true;
    } catch (error) {
      console.error("Failed to load job detail data:", error);
      return true;
    }
  },

  onParamsChange: async ({ rootStore, queryParams, previousQueryParams }) => {
    const viewStore = (rootStore as any).jobDetailViewStore;
    if (!viewStore) return;

    // Handle projectId change
    if (queryParams?.projectId !== previousQueryParams?.projectId) {
      const projectId = queryParams?.projectId as string;
      viewStore.setProjectId(projectId);

      // Reload structures for the new project
      const manholeStructuresStore = (rootStore as any)
        .manholeStructuresStore as ServiceStore;
      if (manholeStructuresStore && projectId) {
        await manholeStructuresStore.find({
          query: { projectId },
        });
      }

      // Re-initialize structure order for the new project
      if (projectId) {
        const manholeViewStore = (rootStore as any)
          .manholeViewStore as ManholeViewStore;
        if (manholeViewStore) {
          await manholeViewStore.initializeStructureOrder(projectId);
        }
      }
    }
  },

  onExit: ({ rootStore }: any) => {
    const viewStore = (rootStore as any).jobDetailViewStore;
    if (viewStore) {
      viewStore.reset();
    }
  },

  dialogs: [
    {
      name: "QuotePreviewDialog",
      component: () =>
        import("../Manhole/dialogs/QuotePreviewDialog"),
      title: "Quote Preview",
      height: 800,
      cancelButton: true,
      submitButtonText: "",
    },
  ],
};

export default JobDetailRoute;
